import { useRequiredContext } from "@redotech/react-util/context";
import { useTriggerLoad } from "@redotech/react-util/load";
import AiStarIcon from "@redotech/redo-icons/ai-star.svg";
import { RedoPlatformAuthRpcClientContext } from "@redotech/redo-merchant-app-common/platform/redo-platform-rpc-client-provider";
import { useAiSlidePanel } from "@redotech/redo-web/ai/chat/contexts/ai-slide-panel-context";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import {
  RedoTextareaInputField,
  RedoTextareaInputFieldSize,
} from "@redotech/redo-web/arbiter-components/input/redo-textarea-input-field";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useState } from "react";
import * as askRedoStyles from "./ask-redo-button.module.css";
import * as css from "./feedback-callout.module.css";

const FEEDBACK_MAX_LENGTH = 500;

export interface FeedbackCalloutProps {
  onClose?: () => void;
}

const OrangeAiStarIcon = () => <AiStarIcon className={askRedoStyles.icon} />;

export const FeedbackCallout = memo(function FeedbackCallout({
  onClose,
}: FeedbackCalloutProps) {
  const platformRpcClient = useRequiredContext(
    RedoPlatformAuthRpcClientContext,
  );
  const { toggle: toggleAiPanel } = useAiSlidePanel();
  const [comment, setComment] = useState("");

  const [submitLoad, handleSubmit] = useTriggerLoad(async () => {
    const trimmed = comment.trim();
    if (!trimmed) {
      return false as const;
    }
    const pageUrl = `${window.location.pathname}${window.location.search}`;
    await platformRpcClient.submitMerchantFeedback({
      comment: trimmed,
      pageUrl,
    });
    return true as const;
  });

  const submitting = submitLoad.pending;
  const submitted = submitLoad.value === true;
  const error =
    !submitting && submitLoad.error
      ? "Something went wrong. Please try again."
      : null;

  const handleAsk = () => {
    onClose?.();
    toggleAiPanel();
  };

  const handleSetComment = (next: string) => {
    setComment(next.slice(0, FEEDBACK_MAX_LENGTH));
  };

  return (
    <Flex
      bgColor="primary"
      borderColor="secondary"
      borderStyle="solid"
      borderWidth="1px"
      className={css.container}
      dir="column"
      gap="xl"
      p="xl"
      radius="xl"
      shadow="lg"
    >
      {submitted ? (
        <>
          <Flex dir="column" gap="xs">
            <Text fontSize="sm" fontWeight="semibold" textColor="primary">
              Thank you for your feedback!
            </Text>
            <Text fontSize="sm" fontWeight="regular" textColor="tertiary">
              We've received your note and our team will look into it. See{" "}
              <a
                className={css.tosLink}
                data-track-id="merchant.top-nav.feedback.common-questions"
                href="https://help.getredo.com/en/"
                rel="noopener noreferrer"
                target="_blank"
              >
                common questions
              </a>{" "}
              for additional support.
            </Text>
          </Flex>
          <Flex align="center" gap="md" justify="flex-end">
            <Text fontSize="xs" fontWeight="regular" textColor="tertiary">
              Still need help?
            </Text>
            <RedoButton
              dataTrackId="merchant.top-nav.feedback.ask"
              hierarchy="secondary"
              IconLeading={OrangeAiStarIcon}
              onClick={handleAsk}
              size="sm"
              text="Ask"
            />
          </Flex>
        </>
      ) : (
        <>
          <RedoTextareaInputField
            containerHeight="var(--redo-spacing-8xl)"
            placeholder="Type your feedback here..."
            setValue={handleSetComment}
            size={RedoTextareaInputFieldSize.SMALL}
            value={comment}
          />
          {error && (
            <Text fontSize="xs" fontWeight="regular" textColor="error">
              {error}
            </Text>
          )}
          <Flex align="center" gap="md" justify="space-between">
            <Text fontSize="xxs" fontWeight="regular" textColor="tertiary">
              Feedback provided is subject to your existing services agreement
              or Redo's{" "}
              <a
                className={css.tosLink}
                href="https://redo.com/terms-and-conditions"
                rel="noopener noreferrer"
                target="_blank"
              >
                Terms of Service
              </a>
              .
            </Text>
            <RedoButton
              dataTrackId="merchant.top-nav.feedback.submit"
              disabled={comment.trim() === "" || submitting}
              hierarchy="primary"
              onClick={() => handleSubmit()}
              size="sm"
              text={submitting ? "Submitting..." : "Submit"}
            />
          </Flex>
        </>
      )}
    </Flex>
  );
});
